import { MenuItem, Select } from '@mui/material'
import { RateFragment, RateType, useRatesQuery } from 'src/api/api.generated'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Typography } from '@vapor/material'

type RateSingleSelectProps = {
  propertyId: string
  value: string | undefined
  onChange: (rateId: string | undefined) => void
  onChangeFragment?: (rate: RateFragment | undefined) => void
  filterRates?: (rate: RateFragment) => boolean
  disabled?: boolean
  rateTypesToShow?: RateType[]
  placeholderWhenEmpty?: string
  small?: boolean
  setBaseRateAsDefault?: true
  hasDefault?: true
  controlledOptions?: string[]
  disableAffiliatedCompanies?: true
  hideAffiliatedCompanies?: boolean
}

export default function RateSingleSelect(props: RateSingleSelectProps) {
  const { id: tenantId } = useTenant()
  const { propertyId, value, onChange, filterRates, disabled, controlledOptions } = props
  const localizeString = useLocalizeString()
  const [selectedRateId, setSelectedRateId] = useState(value ?? '')

  const { data, loading } = useRatesQuery({
    variables: {
      filter: {
        tenantId: {
          eq: tenantId,
        },
        propertyId: {
          eq: propertyId,
        },
      },
    },
    onCompleted: (data) => {
      if (value && value !== '' && data?.rates?.map((cc) => cc.id).includes(value) && selectedRateId === '') {
        setSelectedRateId(value)
      } else if (!value && selectedRateId === '' && props.setBaseRateAsDefault) {
        const baseRate = data?.rates?.find((r) => r.type === 'BASE')
        setSelectedRateId(baseRate?.id ?? '')
        props.onChangeFragment?.(baseRate)
      }
    },
  })

  const disableChoice = useCallback(
    (rate: RateFragment) => {
      const disableAffiliatedCompanies = (props.disableAffiliatedCompanies && (rate.affiliatedCompaniesId ?? []).length > 0) ?? false
      return disableAffiliatedCompanies
    },
    [props.disableAffiliatedCompanies],
  )

  const hideIfAffiliated = useCallback(
    (rate: RateFragment) => {
      const hideAffiliatedCompanies = (props.hideAffiliatedCompanies && (rate.affiliatedCompaniesId ?? []).length > 0) ?? false
      return hideAffiliatedCompanies
    },
    [props.hideAffiliatedCompanies],
  )

  const options = useMemo(() => {
    return (
      data?.rates
        ?.filter((rate) => props.rateTypesToShow === undefined || props.rateTypesToShow.includes(rate.type))
        ?.filter((rate) => controlledOptions === undefined || (controlledOptions !== undefined && controlledOptions.includes(rate.id)))
        ?.filter((rate) => rate.type === 'BASE' || !filterRates || (filterRates && filterRates(rate)))
        ?.filter((rate) => !hideIfAffiliated(rate))
        ?.map((rate) => (
          <MenuItem key={rate.id} value={rate.id} disabled={disableChoice(rate)}>
            <Typography variant="body">{localizeString(rate.name)}</Typography>
          </MenuItem>
        )) ?? []
    )
  }, [data?.rates, props.rateTypesToShow, value, hideIfAffiliated, localizeString])

  useEffect(() => {
    if (data?.rates?.map((r) => r.id).includes(value ?? '')) {
      setSelectedRateId(value ?? '')
    } else if (value === undefined && props.setBaseRateAsDefault) {
      const baseRate = data?.rates?.find((r) => r.type === 'BASE')
      setSelectedRateId(baseRate?.id ?? '')
      props.onChangeFragment?.(baseRate)
    }
  }, [data?.rates, value, props.setBaseRateAsDefault])

  useEffect(() => {
    if (selectedRateId !== value) {
      onChange(selectedRateId)
    }
    props.onChangeFragment?.(data?.rates?.find((r) => r.id === selectedRateId))
  }, [selectedRateId])

  return (
    <Select
      size={props.small ? 'small' : undefined}
      fullWidth
      onChange={(event) => {
        if (options.length > 0) {
          setSelectedRateId(event.target.value)
        }
      }}
      value={options.length > 0 ? selectedRateId : ''}
      disabled={loading || disabled || options.length === 0}
      sx={{
        '& .MuiSelect-select': {
          maxWidth: 'calc(100% - 35px)',
        },
      }}
    >
      {props.hasDefault && (
        <MenuItem key={''} value={''}>
          <Typography variant="body">{props.placeholderWhenEmpty}</Typography>
        </MenuItem>
      )}
      {options.length > 0 && options}
    </Select>
  )
}
